import React, { useState, useEffect, useRef } from 'react';
import ChangePassword from './ChangePassword';
import Footer from './Footer';
import API_URL from '../config/api';
import './ResellerDashboard.css';

export default function ResellerDashboard() {
  const [myUsers, setMyUsers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activatePackage, setActivatePackage] = useState('3months');
  const [activating, setActivating] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [now, setNow] = useState(new Date());
  const USERS_PER_PAGE = 10;
  const menuRef = useRef(null);

  const [newUser, setNewUser] = useState({
    username: '', email: '', password: '', package_type: '3months'
  });

  const token = localStorage.getItem('token');

  const packages = {
    "12hours": { price: 1, label: "12 Hours - $1" },
    "3months": { price: 14, label: "3 Months - $14" },
    "6months": { price: 19, label: "6 Months - $19" },
    "1year": { price: 25, label: "1 Year - $25" }
  };

  // ── TOAST ──
  const showToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  };

  // ── CONFIRM ──
  const showConfirm = (message, onConfirm) => setConfirmDialog({ message, onConfirm });

  // ── CLOCK ──
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setCurrentPage(1); }, [searchQuery, userFilter]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const profileRes = await fetch(`${API_URL}/api/user/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const profileData = await profileRes.json();
      setProfile(profileData.user);

      const usersRes = await fetch(`${API_URL}/api/users/my-users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const usersData = await usersRes.json();
      setMyUsers(usersData.users || []);
    } catch (err) {
      showToast('Failed to fetch data', 'error');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // ── FILTERED + PAGINATED ──
  const allFiltered = myUsers.filter(u => {
    if (userFilter === 'active' && !u.active) return false;
    if (userFilter === 'inactive' && u.active) return false;
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return u.username?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(allFiltered.length / USERS_PER_PAGE);
  const filteredUsers = allFiltered.slice((currentPage - 1) * USERS_PER_PAGE, currentPage * USERS_PER_PAGE);

  // ── COUNTDOWN ──
  const getCountdown = (until) => {
    if (!until) return { text: 'N/A', color: '#999' };
    const diff = new Date(until) - now;
    if (diff <= 0) return { text: '⛔ Expired', color: '#e53935' };

    const days  = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const mins  = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const secs  = Math.floor((diff % (1000 * 60)) / 1000);

    let text;
    if (days > 0)       text = `${days}d ${hours}h ${mins}m ${secs}s`;
    else if (hours > 0) text = `${hours}h ${mins}m ${secs}s`;
    else if (mins > 0)  text = `${mins}m ${secs}s`;
    else                text = `${secs}s`;

    let color;
    if (diff < 3600000)    color = '#e53935';
    else if (diff < 86400000)  color = '#ff9800';
    else if (diff < 259200000) color = '#f39c12';
    else                       color = '#2e7d32';

    return { text, color };
  };

  const getExpiryBadge = (until) => {
    if (!until) return null;
    const diff = new Date(until) - now;
    if (diff <= 0) return { label: '⛔ Expired', cls: 'expired' };
    if (diff < 86400000) return { label: '🔴 < 1 day', cls: 'critical' };
    if (diff < 259200000) return { label: '🟡 < 3 days', cls: 'warning' };
    return null;
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/users/add`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      const data = await response.json();
      if (response.ok) {
        showToast(`User created! Deducted $${data.deducted}. Balance: $${data.remaining_balance}`);
        setShowAddUserModal(false);
        setNewUser({ username: '', email: '', password: '', package_type: '3months' });
        fetchData(true);
      } else {
        showToast(data.message || 'Failed to create user', 'error');
      }
    } catch (err) {
      showToast('Failed to add user', 'error');
    }
  };

  const handleActivateUser = async () => {
    if (!selectedUser) return;
    setActivating(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${selectedUser._id}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_type: activatePackage })
      });
      const data = await response.json();
      if (response.ok) {
        showToast(`✅ ${selectedUser.username} activated with ${packages[activatePackage].label}`);
        setShowActivateModal(false);
        setSelectedUser(null);
        fetchData(true);
      } else {
        showToast(data.message || 'Failed to activate user', 'error');
      }
    } catch (err) {
      showToast('Failed to activate user', 'error');
    } finally {
      setActivating(false);
    }
  };

  const openActivateModal = (user) => {
    if (user.active) return;
    setSelectedUser(user);
    setActivatePackage('3months');
    setShowActivateModal(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const closeMenu = (fn) => { fn(); setMobileMenuOpen(false); };

  // counts
  const activeCount = myUsers.filter(u => u.active).length;
  const inactiveCount = myUsers.filter(u => !u.active).length;
  const expiringSoon = myUsers.filter(u => {
    if (!u.available_until) return false;
    const diff = new Date(u.available_until) - now;
    return diff > 0 && diff < 259200000;
  }).length;

  if (loading) return <div className="rd-loading">Loading...</div>;

  return (
    <div className="rd-root">

      {/* HEADER */}
      <header className="rd-header">
        <div className="rd-header-brand">
          <span className="rd-brand-icon">🏪</span>
          <span className="rd-brand-name">Tiger Guard</span>
        </div>
        <div className="rd-header-desktop-actions">
          <div className="rd-pill">
            <span className="rd-pill-icon">💰</span>
            <strong>${profile?.wallet || 0}</strong>
            <span className="rd-pill-label">Balance</span>
          </div>
          <div className="rd-pill">
            <span className="rd-pill-icon">👥</span>
            <strong>{myUsers.length}</strong>
            <span className="rd-pill-label">Users</span>
          </div>
          <span className="rd-username-badge">{profile?.username}</span>
          <span className="rd-role-badge">Reseller</span>
          <button className="rd-btn-icon" title="Change Password" onClick={() => setShowChangePassword(true)}>🔐</button>
          <button className="rd-btn-logout" onClick={handleLogout}>Logout</button>
        </div>
        <button className={`rd-hamburger ${mobileMenuOpen ? 'open' : ''}`} onClick={() => setMobileMenuOpen(v => !v)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </header>

      {/* MOBILE DRAWER */}
      <div className={`rd-mobile-drawer ${mobileMenuOpen ? 'visible' : ''}`} ref={menuRef}>
        <div className="rd-drawer-profile">
          <div className="rd-drawer-avatar">🏪</div>
          <div>
            <div className="rd-drawer-username">{profile?.username}</div>
            <div className="rd-role-badge" style={{ marginTop: 4 }}>Reseller</div>
          </div>
        </div>
        <div className="rd-drawer-stats">
          <div className="rd-drawer-stat">
            <span className="rd-drawer-stat-icon">💰</span>
            <div><div className="rd-drawer-stat-value">${profile?.wallet || 0}</div><div className="rd-drawer-stat-label">Balance</div></div>
          </div>
          <div className="rd-drawer-stat">
            <span className="rd-drawer-stat-icon">👥</span>
            <div><div className="rd-drawer-stat-value">{myUsers.length}</div><div className="rd-drawer-stat-label">Users</div></div>
          </div>
          <div className="rd-drawer-stat">
            <span className="rd-drawer-stat-icon">✅</span>
            <div><div className="rd-drawer-stat-value">{activeCount}</div><div className="rd-drawer-stat-label">Active</div></div>
          </div>
        </div>
        <div className="rd-drawer-actions">
          <button className="rd-drawer-btn" onClick={() => closeMenu(() => setShowAddUserModal(true))}>➕ Add User</button>
          <button className="rd-drawer-btn secondary" onClick={() => closeMenu(() => setShowChangePassword(true))}>🔐 Change Password</button>
          <button className="rd-drawer-btn danger" onClick={handleLogout}>🚪 Logout</button>
        </div>
      </div>

      {mobileMenuOpen && <div className="rd-backdrop" onClick={() => setMobileMenuOpen(false)} />}

      {/* MAIN */}
      <main className="rd-main">

        {/* Stats cards */}
        <div className="rd-stats-grid">
          <div className="rd-stat-card">
            <div className="rd-stat-icon">💵</div>
            <div><div className="rd-stat-label">Available Balance</div><div className="rd-stat-value">${profile?.wallet || 0}</div></div>
          </div>
          <div className="rd-stat-card">
            <div className="rd-stat-icon">💸</div>
            <div><div className="rd-stat-label">Total Spent</div><div className="rd-stat-value">${profile?.total_spent || 0}</div></div>
          </div>
          <div className="rd-stat-card">
            <div className="rd-stat-icon">✅</div>
            <div><div className="rd-stat-label">Active Users</div><div className="rd-stat-value">{activeCount}</div></div>
          </div>
          <div className="rd-stat-card">
            <div className="rd-stat-icon">⚠️</div>
            <div><div className="rd-stat-label">Expiring Soon</div><div className="rd-stat-value" style={{ color: expiringSoon > 0 ? '#f39c12' : '#2e7d32' }}>{expiringSoon}</div></div>
          </div>
        </div>

        {/* Users section */}
        <div className="rd-section">
          <div className="rd-section-header">
            <h2>My Users</h2>
            <button className="rd-btn-primary" onClick={() => setShowAddUserModal(true)}>➕ Add User</button>
          </div>

          {/* Filter tabs */}
          <div className="rd-filter-tabs">
            <button className={`rd-filter-tab ${userFilter === 'all' ? 'active' : ''}`} onClick={() => setUserFilter('all')}>
              All <span className="rd-filter-count">{myUsers.length}</span>
            </button>
            <button className={`rd-filter-tab ${userFilter === 'active' ? 'active' : ''}`} onClick={() => setUserFilter('active')}>
              ✓ Active <span className="rd-filter-count">{activeCount}</span>
            </button>
            <button className={`rd-filter-tab ${userFilter === 'inactive' ? 'active' : ''}`} onClick={() => setUserFilter('inactive')}>
              ✗ Inactive <span className="rd-filter-count">{inactiveCount}</span>
            </button>
          </div>

          {/* Search bar */}
          <div className="rd-search-wrap">
            <span className="rd-search-icon">🔍</span>
            <input
              type="text"
              className="rd-search-input"
              placeholder="Search by username or email..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && <button className="rd-search-clear" onClick={() => setSearchQuery('')}>✕</button>}
          </div>

          {/* Desktop table */}
          <div className="rd-table-wrap">
            <table className="rd-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Package</th>
                  <th>Time Left</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="rd-empty-row">
                      {searchQuery ? `No users found for "${searchQuery}"` : 'No users yet. Click "Add User" to get started!'}
                    </td>
                  </tr>
                ) : filteredUsers.map(user => {
                  const countdown = getCountdown(user.available_until);
                  const badge = getExpiryBadge(user.available_until);
                  return (
                    <tr key={user._id}>
                      <td>{user.username}</td>
                      <td>{user.email || 'N/A'}</td>
                      <td>{user.package_type || 'N/A'}</td>
                      <td>
                        <span style={{ color: countdown.color, fontWeight: 600, fontFamily: 'monospace', fontSize: '13px' }}>
                          {countdown.text}
                        </span>
                        {badge && <span className={`rd-expiry-badge ${badge.cls}`}>{badge.label}</span>}
                      </td>
                      <td>
                        <span className={`rd-status ${user.active ? 'active' : 'inactive'}`}>
                          {user.active ? '✓ Active' : '✗ Inactive'}
                        </span>
                      </td>
                      <td>
                        {new Date(user.created_at).toLocaleDateString()}{' '}
                        {new Date(user.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td>
                        <button
                          className="rd-btn-activate"
                          onClick={() => openActivateModal(user)}
                          disabled={user.active}
                          style={{ opacity: user.active ? 0.4 : 1, cursor: user.active ? 'not-allowed' : 'pointer' }}
                        >
                          ⚡ Activate
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="rd-mobile-cards">
            {filteredUsers.length === 0 ? (
              <div className="rd-empty-card">
                {searchQuery ? `No users found for "${searchQuery}"` : 'No users yet. Click "Add User" to get started!'}
              </div>
            ) : filteredUsers.map(user => {
              const countdown = getCountdown(user.available_until);
              const badge = getExpiryBadge(user.available_until);
              return (
                <div className="rd-user-card" key={user._id}>
                  <div className="rd-user-card-header">
                    <span className="rd-user-card-name">{user.username}</span>
                    <span className={`rd-status ${user.active ? 'active' : 'inactive'}`}>
                      {user.active ? '✓ Active' : '✗ Inactive'}
                    </span>
                  </div>
                  <div className="rd-user-card-body">
                    <div className="rd-user-card-row">
                      <span className="rd-user-card-key">Email</span>
                      <span className="rd-user-card-val">{user.email || 'N/A'}</span>
                    </div>
                    <div className="rd-user-card-row">
                      <span className="rd-user-card-key">Package</span>
                      <span className="rd-user-card-val">{user.package_type || 'N/A'}</span>
                    </div>
                    <div className="rd-user-card-row">
                      <span className="rd-user-card-key">Time Left</span>
                      <span className="rd-user-card-val" style={{ color: countdown.color, fontFamily: 'monospace', fontWeight: 600 }}>
                        {countdown.text}
                        {badge && <span className={`rd-expiry-badge ${badge.cls}`} style={{ marginLeft: 6 }}>{badge.label}</span>}
                      </span>
                    </div>
                    <div className="rd-user-card-row">
                      <span className="rd-user-card-key">Created</span>
                      <span className="rd-user-card-val">
                        {new Date(user.created_at).toLocaleDateString()}{' '}
                        {new Date(user.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <button
                      className="rd-btn-activate rd-btn-activate-full"
                      onClick={() => openActivateModal(user)}
                      disabled={user.active}
                      style={{ opacity: user.active ? 0.4 : 1, cursor: user.active ? 'not-allowed' : 'pointer' }}
                    >
                      {user.active ? '✓ Already Active' : '⚡ Activate'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="rd-pagination">
              <button className="rd-page-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>‹ Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button key={page} className={`rd-page-btn ${currentPage === page ? 'active' : ''}`} onClick={() => setCurrentPage(page)}>{page}</button>
              ))}
              <button className="rd-page-btn" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>Next ›</button>
              <span className="rd-page-info">Page {currentPage} of {totalPages} · {allFiltered.length} users</span>
            </div>
          )}
        </div>
      </main>

      {/* ADD USER MODAL */}
      {showAddUserModal && (
        <div className="rd-overlay" onClick={() => setShowAddUserModal(false)}>
          <div className="rd-modal" onClick={e => e.stopPropagation()}>
            <div className="rd-modal-header">
              <h3>Add New User</h3>
              <button className="rd-modal-close" onClick={() => setShowAddUserModal(false)}>✕</button>
            </div>
            <form onSubmit={handleAddUser}>
              <input type="text" placeholder="Username" value={newUser.username} onChange={e => setNewUser({ ...newUser, username: e.target.value })} required />
              <input type="email" placeholder="Email (optional)" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} />
              <input type="password" placeholder="Password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} required />
              <select value={newUser.package_type} onChange={e => setNewUser({ ...newUser, package_type: e.target.value })} required>
                {Object.entries(packages).map(([key, pkg]) => (
                  <option key={key} value={key}>{pkg.label}</option>
                ))}
              </select>
              <p className="rd-hint">
                Cost: <strong>${packages[newUser.package_type].price}</strong> &nbsp;|&nbsp;
                Your balance: <strong>${profile?.wallet || 0}</strong>
              </p>
              <div className="rd-modal-actions">
                <button type="button" onClick={() => setShowAddUserModal(false)}>Cancel</button>
                <button type="submit" className="rd-btn-primary">Create User</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ACTIVATE USER MODAL */}
      {showActivateModal && selectedUser && (
        <div className="rd-overlay" onClick={() => setShowActivateModal(false)}>
          <div className="rd-modal" onClick={e => e.stopPropagation()}>
            <div className="rd-modal-header">
              <h3>⚡ Activate User</h3>
              <button className="rd-modal-close" onClick={() => setShowActivateModal(false)}>✕</button>
            </div>
            <div style={{ padding: '18px 22px 22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div className="rd-activate-user-info">
                <span className="rd-activate-avatar">👤</span>
                <div>
                  <div className="rd-activate-name">{selectedUser.username}</div>
                  <div className="rd-activate-email">{selectedUser.email || 'No email'}</div>
                  <span className="rd-status inactive" style={{ marginTop: 4, display: 'inline-block' }}>✗ Currently Inactive</span>
                </div>
              </div>
              <label className="rd-field-label">Select Package</label>
              <div className="rd-package-grid">
                {Object.entries(packages).map(([key, pkg]) => (
                  <div key={key} className={`rd-package-option ${activatePackage === key ? 'selected' : ''}`} onClick={() => setActivatePackage(key)}>
                    <div className="rd-package-label">{pkg.label.split(' - ')[0]}</div>
                    <div className="rd-package-price">${pkg.price}</div>
                  </div>
                ))}
              </div>
              <p className="rd-hint">
                Cost: <strong>${packages[activatePackage].price}</strong> &nbsp;|&nbsp;
                Your balance: <strong>${profile?.wallet || 0}</strong> &nbsp;|&nbsp;
                After: <strong>${Math.max(0, (profile?.wallet || 0) - packages[activatePackage].price)}</strong>
              </p>
              {(profile?.wallet || 0) < packages[activatePackage].price && (
                <div className="rd-alert error" style={{ margin: 0 }}>⚠️ Insufficient balance for this package.</div>
              )}
              <div className="rd-modal-actions">
                <button type="button" onClick={() => setShowActivateModal(false)}>Cancel</button>
                <button
                  className="rd-btn-primary"
                  onClick={handleActivateUser}
                  disabled={activating || (profile?.wallet || 0) < packages[activatePackage].price}
                >
                  {activating ? 'Activating...' : `⚡ Activate - $${packages[activatePackage].price}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM DIALOG */}
      {confirmDialog && (
        <div className="rd-overlay" onClick={() => setConfirmDialog(null)}>
          <div className="rd-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="rd-confirm-icon">⚠️</div>
            <div className="rd-confirm-message">{confirmDialog.message}</div>
            <div className="rd-confirm-actions">
              <button className="rd-confirm-cancel" onClick={() => setConfirmDialog(null)}>Cancel</button>
              <button className="rd-confirm-ok" onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }}>Confirm</button>
            </div>
          </div>
        </div>
      )}

      <ChangePassword isOpen={showChangePassword} onClose={() => setShowChangePassword(false)} onSuccess={msg => showToast(msg)} />
      <Footer variant="reseller" />

      {/* TOAST NOTIFICATIONS */}
      <div className="rd-toast-container">
        {toasts.map(toast => (
          <div key={toast.id} className={`rd-toast ${toast.type}`}>
            <span>{toast.type === 'success' ? '✅' : '❌'}</span>
            <span>{toast.message}</span>
            <button onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}